import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { chatAPI } from '../services/api';

/**
 * ChatBoard - reusable message-board component.
 *
 * Props:
 *   raceId  {string|null}  - if provided, loads race-specific chat; otherwise general chat
 *   title   {string}       - heading text (optional)
 */
function ChatBoard({ raceId = null, title }) {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [canModerate, setCanModerate] = useState(false);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [posting, setPosting] = useState(false);
  const [error, setError] = useState('');
  const POLL_INTERVAL = 15000; // 15 seconds
  const messagesListRef = useRef(null);

  // Scroll to bottom whenever messages update
  useEffect(() => {
    if (messagesListRef.current) {
      messagesListRef.current.scrollTop = messagesListRef.current.scrollHeight;
    }
  }, [messages]);

  const loadMessages = useCallback(async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      const response = raceId
        ? await chatAPI.getRaceMessages(raceId)
        : await chatAPI.getGeneralMessages();

      if (response.success) {
        setMessages(response.data.messages || []);
        setCanModerate(response.data.canModerate || false);
      }
    } catch (err) {
      if (!silent) setError('Failed to load messages.');
      console.error('Chat load error:', err);
    } finally {
      if (!silent) setLoading(false);
    }
  }, [raceId]);

  // Initial load
  useEffect(() => {
    loadMessages();
  }, [loadMessages]);

  // Poll for new messages
  useEffect(() => {
    const interval = setInterval(() => loadMessages(true), POLL_INTERVAL);
    return () => clearInterval(interval);
  }, [loadMessages]);

  const handlePost = async (e) => {
    e.preventDefault();
    const text = input.trim();
    if (!text) return;

    try {
      setPosting(true);
      setError('');
      const response = await chatAPI.postMessage({
        message: text,
        raceId: raceId || undefined,
      });
      if (response.success) {
        setInput('');
        await loadMessages(true);
      } else {
        setError(response.error?.message || 'Failed to post message.');
      }
    } catch (err) {
      setError(err.response?.data?.error?.message || 'Failed to post message.');
    } finally {
      setPosting(false);
    }
  };

  const handleDelete = async (messageId) => {
    if (!window.confirm('Delete this message?')) return;
    try {
      const response = await chatAPI.deleteMessage(messageId);
      if (response.success) {
        setMessages((prev) => prev.filter((m) => m.messageId !== messageId));
      }
    } catch (err) {
      alert('Failed to delete message.');
    }
  };

  const formatTime = (iso) => {
    const d = new Date(iso);
    const today = new Date();
    const isToday =
      d.getFullYear() === today.getFullYear() &&
      d.getMonth() === today.getMonth() &&
      d.getDate() === today.getDate();
    return isToday
      ? d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      : d.toLocaleDateString([], { month: 'short', day: 'numeric' }) +
          ' ' +
          d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      {title && (
        <h3 style={{ margin: '0 0 1rem 0' }}>{title}</h3>
      )}

      {/* Message list */}
      <div
        ref={messagesListRef}
        style={{
          flex: 1,
          overflowY: 'auto',
          maxHeight: '420px',
          minHeight: '120px',
          border: '1px solid #e0e0e0',
          borderRadius: '8px',
          padding: '0.75rem',
          backgroundColor: '#fafafa',
          marginBottom: '0.75rem',
        }}
      >
        {loading ? (
          <p style={{ color: '#999', textAlign: 'center', margin: '2rem 0' }}>Loading messages…</p>
        ) : messages.length === 0 ? (
          <p style={{ color: '#aaa', textAlign: 'center', fontStyle: 'italic', margin: '2rem 0' }}>
            No messages yet. Be the first to post!
          </p>
        ) : (
          messages.map((msg) => {
            const isOwn = msg.userId === user?.userId;
            const canDelete = canModerate || isOwn;
            return (
              <div
                key={msg.messageId}
                style={{
                  display: 'flex',
                  justifyContent: isOwn ? 'flex-end' : 'flex-start',
                  marginBottom: '0.6rem',
                }}
              >
                <div
                  style={{
                    maxWidth: '75%',
                    backgroundColor: isOwn ? '#1976d2' : '#fff',
                    color: isOwn ? '#fff' : '#222',
                    border: isOwn ? 'none' : '1px solid #e0e0e0',
                    borderRadius: isOwn ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                    padding: '0.6rem 0.9rem',
                    boxShadow: '0 1px 2px rgba(0,0,0,0.07)',
                    position: 'relative',
                  }}
                >
                  {!isOwn && (
                    <div
                      style={{
                        fontWeight: 'bold',
                        fontSize: '0.8rem',
                        marginBottom: '0.2rem',
                        color: '#1976d2',
                      }}
                    >
                      {msg.displayName || msg.username}
                    </div>
                  )}
                  <div style={{ fontSize: '0.95rem', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                    {msg.message}
                  </div>
                  <div
                    style={{
                      fontSize: '0.72rem',
                      marginTop: '0.3rem',
                      opacity: 0.65,
                      textAlign: isOwn ? 'right' : 'left',
                    }}
                  >
                    {formatTime(msg.createdAt)}
                    {canDelete && (
                      <button
                        onClick={() => handleDelete(msg.messageId)}
                        title="Delete message"
                        style={{
                          marginLeft: '0.6rem',
                          background: 'transparent',
                          border: 'none',
                          cursor: 'pointer',
                          color: isOwn ? 'rgba(255,255,255,0.7)' : '#f44336',
                          fontSize: '0.75rem',
                          padding: 0,
                          verticalAlign: 'middle',
                        }}
                      >
                        🗑
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Error */}
      {error && (
        <div style={{ color: '#f44336', fontSize: '0.9rem', marginBottom: '0.5rem' }}>
          {error}
        </div>
      )}

      {/* Input */}
      <form onSubmit={handlePost} style={{ display: 'flex', gap: '0.5rem' }}>
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          maxLength={1000}
          placeholder="Type a message…"
          disabled={posting}
          style={{
            flex: 1,
            padding: '0.6rem 0.8rem',
            borderRadius: '20px',
            border: '1px solid #ddd',
            fontSize: '0.95rem',
            outline: 'none',
          }}
        />
        <button
          type="submit"
          disabled={posting || !input.trim()}
          style={{
            padding: '0.6rem 1.2rem',
            borderRadius: '20px',
            background: posting || !input.trim() ? '#ccc' : '#1976d2',
            color: 'white',
            border: 'none',
            cursor: posting || !input.trim() ? 'not-allowed' : 'pointer',
            fontWeight: 'bold',
            fontSize: '0.95rem',
            whiteSpace: 'nowrap',
          }}
        >
          {posting ? '…' : 'Send'}
        </button>
      </form>
      <div style={{ textAlign: 'right', fontSize: '0.75rem', color: '#bbb', marginTop: '0.25rem' }}>
        {input.length}/1000 • refreshes every 15s
      </div>
    </div>
  );
}

export default ChatBoard;
